// Google Maps takes styles as a JSON array of literal colors, which would
// otherwise mean a second copy of the dark palette living in JavaScript. These
// read the --cc-map-* custom properties off the document instead, so index.css
// stays the only place the basemap colors are written down.
//
// Resolved lazily rather than at module scope: the stylesheet is not guaranteed
// to have been applied when this module is first evaluated.
const readComputedToken = name => getComputedStyle(document.documentElement)
  .getPropertyValue(name)
  .trim();

// readToken is injectable so the shape of the style array can be tested without
// depending on jsdom resolving custom properties.
export const buildDarkMapStyle = (readToken = readComputedToken) => {
  const land = readToken('--cc-map-land');
  const water = readToken('--cc-map-water');
  const park = readToken('--cc-map-park');
  const road = readToken('--cc-map-road');
  const roadMajor = readToken('--cc-map-road-major');
  const label = readToken('--cc-map-label');
  const labelStroke = readToken('--cc-map-label-stroke');
  const transit = readToken('--cc-map-transit');

  // A missing token means the dark block is not applied; fall back to Google's
  // default styling rather than painting an all-black map.
  if (!land || !water || !label) return null;

  return [
    { elementType: 'geometry', stylers: [{ color: land }] },
    // Every label gets a dark halo so place names stay readable wherever they
    // land — over water, park, or road.
    { elementType: 'labels.text.fill', stylers: [{ color: label }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: labelStroke }] },
    // Google's own POI icons are tuned for the light basemap and turn into
    // confetti over a dark one, next to our availability markers.
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: transit }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: park }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: road }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: land }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: roadMajor }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: roadMajor }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: transit }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: water }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: label }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: roadMajor }] },
  ];
};

export default buildDarkMapStyle;
